import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';

import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';

import { setSystemUserPreferences } from '@services/commonService';

import { getUserPreference } from '@utils/Util';
import { STATIC_SET_CONSTANTS } from '@utils/Util';

import CommonImage from '@/components/CommonImage';
import HorizontalScroll from '@/components/HorizontalScroll';
import LoaderComponent from '@/components/LoaderComponent';
import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

import { COSTING_MIX_PANEL_EVENTS } from '../utils';
import { setFobDecisionResponse } from '../redux/costingAgentSlice';
import {
	useGetSimilarTypesStyleQuery,
	useGetSimilarTableDataQuery,
	useGetFindSimilarInformationQuery,
	useSetFobDecisionMutation
} from '../api/costingAgentApi';

import FobDecisionPanel from './FobDecisionPanel';

const MarketContextTarget = ({
	costingBrandInfo,
	productId,
	onViewChange,
	productionIsloading,
	onProductionCostingDisabledChange
}) => {
	const [similarTypeData, setSimilarTypeData] = useState(null);
	const [similarTableData, setSimilarTableData] = useState(null);
	const [findFobResponse, setFindFobResponse] = useState(null);
	const location = useLocation();
	const dispatch = useDispatch();
	const [fobDecisionDetail, setFobDecisionaDetail] = useState({});
	const userPreference = getUserPreference();
	const [shippingTypeItem, setShippingTypeItem] = useState(userPreference.SHIPPING_TYPE_ITEM || 'VALUE');
	const [isCommercialCostVisible, setIsCommercialCostVisible] = useState(
		!!userPreference?.IS_COMMERCIAL_COST_VISIBLE
	);
	const similarStylesRef = useRef(null);
	const historyTableRef = useRef(null);
	const hasInitialFobDecisionCall = useRef(false);

	const toNumberOrNull = (value) => {
		if (value === '' || value === null || value === undefined) return null;
		const number = Number(value);
		return Number.isNaN(number) ? null : number;
	};

	const calculateNetValue = (retailReferencePrice, vatPercentage) => {
		const retail = toNumberOrNull(retailReferencePrice);
		const vat = toNumberOrNull(vatPercentage) ?? 0;
		if (retail === null) return '';
		return (retail / (1 + vat / 100)).toFixed(2);
	};

	const calculateNetRetailUsd = (netValue, usdConversionRate) => {
		const net = toNumberOrNull(netValue);
		const usd = toNumberOrNull(usdConversionRate);
		if (net === null || usd === null) return '';
		return Math.round(net * usd);
	};

	const calculateTargetLandedCost = (netRetailUsd, retailPercentage) => {
		const net = toNumberOrNull(netRetailUsd);
		const margin = toNumberOrNull(retailPercentage);
		if (net === null || margin === null) return '';
		return (net * (1 - margin / 100)).toFixed(2);
	};

	const calculateSuggestedFob = (
		targetLandedCost,
		shippingPercentage,
		warehousePercentage,
		dutyPercentage,
		otherCostPercentage
	) => {
		const landedCost = toNumberOrNull(targetLandedCost);
		const logistics = toNumberOrNull(shippingPercentage);
		const freight = toNumberOrNull(warehousePercentage) ?? 0;
		const duty = toNumberOrNull(dutyPercentage) ?? 0;
		const other = toNumberOrNull(otherCostPercentage) ?? 0;

		if (landedCost === null) return '';
		const denominator = 1 + (logistics + freight + duty + other) / 100;
		return (landedCost / denominator).toFixed(2);
	};

	const calculateSuggestedFobWithCommercialCost = (targetLandedCost, commercialPercentage) => {
		const landedCost = toNumberOrNull(targetLandedCost);
		const commercial = toNumberOrNull(commercialPercentage) ?? 0;

		if (landedCost === null) return '';
		const denominator = 1 + commercial / 100;
		return (landedCost / denominator).toFixed(2);
	};

	const calculateSuggestedFobWithVlue = (
		targetLandedCost,
		shippingValue,
		warehousePercentage,
		dutyPercentage,
		otherCostPercentage
	) => {
		const landedCost = toNumberOrNull(targetLandedCost);
		const shipping = toNumberOrNull(shippingValue);
		const freight = toNumberOrNull(warehousePercentage) ?? 0;
		const duty = toNumberOrNull(dutyPercentage) ?? 0;
		const other = toNumberOrNull(otherCostPercentage) ?? 0;

		if (landedCost === null || shipping === null) return '';
		const denominator = 1 + (freight + duty + other) / 100;
		return ((landedCost - shipping) / denominator).toFixed(2);
	};

	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const { data: findSimilartyInformation, isFetching: isFindSimilarFetching } =
		useGetFindSimilarInformationQuery(
			{
				productId,
				initialCostingId: costingBrandInfo?.initialCostingId
			},
			{ skip: !productId }
		);

	const findSimilarityPayload = findSimilartyInformation?.payload;
	const hasFindSimilarityPayload = !!findSimilarityPayload && Object.keys(findSimilarityPayload).length > 0;
	const shouldFetchFallbackData = !isFindSimilarFetching && !hasFindSimilarityPayload;

	const { data: similarTypesStyleData, isFetching: isSimilarTypesStyleFetching } =
		useGetSimilarTypesStyleQuery(
			{
				productId,
				brandName: costingBrandInfo?.brandName,
				quantity: costingBrandInfo?.singleQty,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				initialCostingId: costingBrandInfo?.initialCostingId
			},
			{
				skip:
					!productId ||
					!costingBrandInfo?.brandName ||
					!costingBrandInfo?.singleQty ||
					!shouldFetchFallbackData
			}
		);

	const { data: similarTableDataResponse, isFetching: isSimilarTableDataFetching } =
		useGetSimilarTableDataQuery(
			{
				productId,
				brandName: costingBrandInfo?.brandName,
				quantity: costingBrandInfo?.singleQty,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				initialCostingId: costingBrandInfo?.initialCostingId
			},
			{
				skip:
					!productId ||
					!costingBrandInfo?.brandName ||
					!costingBrandInfo?.singleQty ||
					!shouldFetchFallbackData
			}
		);

	const [setFobDecision, { isLoading: isSetFobDecisionLoading }] = useSetFobDecisionMutation();

	const isNilOrEmpty = (value) => value === '' || value === null || value === undefined;

	const hasEmptyRequiredFobInput = [
		fobDecisionDetail?.retailReferencePrice,
		fobDecisionDetail?.usdConversionRate ?? fobDecisionDetail?.usdConReference,
		fobDecisionDetail?.retailPercentage
	].some(isNilOrEmpty);

	const isTargetedFobPriceEmpty = !(Number(fobDecisionDetail?.targetedFobPrice) > 0);
	const isEmptyFobInput = hasEmptyRequiredFobInput || isTargetedFobPriceEmpty;

	useEffect(() => {
		onProductionCostingDisabledChange?.(isTargetedFobPriceEmpty);
	}, [isTargetedFobPriceEmpty, onProductionCostingDisabledChange]);

	const handleFobSubmit = async ({
		showSuccessToast = false,
		showErrorToast = true,
		includeShippingType = true
	} = {}) => {
		try {
			const requestBody = {
				...fobDecisionDetail,
				shippingType: shippingTypeItem
			};

			if (includeShippingType) {
				// these percentages are optional in the UI — send 0 instead of omitting them
				['vatPercentage', 'warehousePercentage', 'dutyPercentage', 'otherCostPercentage'].forEach(
					(key) => {
						requestBody[key] = toNumberOrNull(requestBody[key]) ?? 0;
					}
				);
			}

			if (!includeShippingType) {
				delete requestBody.shippingType;
			}

			if (shippingTypeItem === 'PERCENTAGE') {
				delete requestBody.shippingValue;
			} else {
				delete requestBody.shippingPercentage;
			}

			// commercial costing replaces the shipping breakdown, so none of it reaches the API
			if (isCommercialCostVisible) {
				[
					'shippingType',
					'shippingValue',
					'shippingPercentage',
					'warehousePercentage',
					'dutyPercentage',
					'otherCostPercentage'
				].forEach((key) => delete requestBody[key]);
			}

			const response = await setFobDecision({
				productId,
				brandName: costingBrandInfo?.brandName,
				initialCostingId: costingBrandInfo?.initialCostingId,
				requestBody
			}).unwrap();

			setFindFobResponse(response);
			dispatch(setFobDecisionResponse(response?.payload));
			if (showSuccessToast) {
				toastSuccess('FOB decision calculated successfully');
			}
			return response;
		} catch (error) {
			if (showErrorToast) {
				toastError(error?.data?.message || error?.message || 'Failed to calculate FOB.');
			}
			return null;
		}
	};

	const fobDecisionResponse = findFobResponse?.payload ?? '';
	const similarMedianRetailPrice = similarTypeData?.medianRetailPrice ?? '';
	useEffect(() => {
		if (findFobResponse?.payload) {
			const resp = fobDecisionResponse;
			const selectedShippingType =
				userPreference.SHIPPING_TYPE_ITEM || resp?.shippingType || shippingTypeItem;
			const usdRateRaw =
				resp?.usdConversionRate ?? resp?.usdConReference ?? similarTypeData?.usdConversionRate;
			const usdRate = usdRateRaw != null ? Number(usdRateRaw).toFixed(2) : usdRateRaw;
			const retailReferencePrice = resp?.retailReferencePrice ?? similarTypeData?.medianRetailPrice;
			const netVal = calculateNetValue(retailReferencePrice, resp?.vatPercentage);
			const netRetail = calculateNetRetailUsd(netVal, usdRate);
			const targetLanded = calculateTargetLandedCost(netRetail, resp?.retailPercentage);
			const suggestedFob =
				resp?.targetedFobPrice ??
				(isCommercialCostVisible
					? calculateSuggestedFobWithCommercialCost(targetLanded, resp?.commercialPercentage)
					: calculateSuggestedFob(
							targetLanded,
							resp?.shippingPercentage,
							resp?.warehousePercentage,
							resp?.dutyPercentage,
							resp?.otherCostPercentage
						));

			const suggestedFobNum = toNumberOrNull(suggestedFob);
			const minRange = suggestedFobNum !== null ? (suggestedFobNum * 0.95).toFixed(2) : null;
			const maxRange = suggestedFobNum !== null ? (suggestedFobNum * 1.05).toFixed(2) : null;
			const rangeText =
				minRange && maxRange ? `$${minRange} - $${maxRange}` : (resp?.buyerBuyingRange ?? '');

			setShippingTypeItem(selectedShippingType);

			setFobDecisionaDetail((currentDetails) => ({
				...currentDetails,
				medianRetailPrice: similarMedianRetailPrice || 0,
				retailReferencePrice: retailReferencePrice,
				vatPercentage: resp?.vatPercentage,
				netValue: netVal,
				usdConversionRate: usdRate,
				usdConReference: usdRate,
				netRetailUsd: netRetail,
				retailPercentage: resp?.retailPercentage,
				targetLandedCost: targetLanded,
				commercialPercentage: resp?.commercialPercentage,
				shippingPercentage: resp?.shippingPercentage,
				shippingValue: resp?.shippingValue,
				shippingType: selectedShippingType,
				warehousePercentage: resp?.warehousePercentage,
				dutyPercentage: resp?.dutyPercentage,
				otherCostPercentage: resp?.otherCostPercentage,
				buyerBuyingRange: rangeText,
				targetedFobPrice: suggestedFob
			}));
		}
	}, [findFobResponse, similarMedianRetailPrice]);

	const isMarketContextLoading =
		isFindSimilarFetching ||
		(shouldFetchFallbackData && (isSimilarTypesStyleFetching || isSimilarTableDataFetching));

	// empty similar styles leave medianRetailPrice null — still call once the similarity data has settled
	useEffect(() => {
		if (
			!hasInitialFobDecisionCall.current &&
			productId &&
			costingBrandInfo?.brandName &&
			(similarTypeData?.medianRetailPrice != null || !isMarketContextLoading)
		) {
			hasInitialFobDecisionCall.current = true;
			// background prefill — the user didn't trigger it, so don't surface its failure as a toast
			handleFobSubmit({ includeShippingType: false, showErrorToast: false });
		}
	}, [productId, costingBrandInfo?.brandName, similarTypeData?.medianRetailPrice, isMarketContextLoading]);

	useEffect(() => {
		if (hasFindSimilarityPayload) {
			setSimilarTypeData(findSimilarityPayload?.similarStyleResponse || null);
			setSimilarTableData(findSimilarityPayload?.similarOrderResponse || null);
			return;
		}

		if (similarTypesStyleData?.payload) {
			setSimilarTypeData(similarTypesStyleData.payload);
		}
		if (similarTableDataResponse?.payload) {
			setSimilarTableData(similarTableDataResponse.payload);
		}
	}, [hasFindSimilarityPayload, findSimilarityPayload, similarTypesStyleData, similarTableDataResponse]);

	const similarProducts = similarTypeData?.similarProducts || [];
	const stylesToRender = similarProducts.map((style) => ({
		match: `${Math.round(style.matchingScore)}%`,
		name: style.title,
		sku: style.productId,
		oldPrice: `${style.initialPrice?.toFixed(2)}`,
		newPrice: `${style.latestPrice?.toFixed(2)}`,
		image: style.productImageUrl,
		productUrl: style.productUrl,
		currency: style.currency
	}));

	const similarBrandName =
		new URLSearchParams(location.search).get('brandName') || costingBrandInfo?.brandName;
	const handleRetailRefClick = (mediunRefPrice) => {
		if (mediunRefPrice) {
			handleFobDesision('retailReferencePrice', mediunRefPrice);
		}
	};

	const suggestedFobPrice =
		(isCommercialCostVisible
			? calculateSuggestedFobWithCommercialCost(
					fobDecisionDetail?.targetLandedCost,
					fobDecisionDetail?.commercialPercentage
				)
			: shippingTypeItem === 'VALUE'
				? calculateSuggestedFobWithVlue(
						fobDecisionDetail?.targetLandedCost,
						fobDecisionDetail?.shippingValue,
						fobDecisionDetail?.warehousePercentage,
						fobDecisionDetail?.dutyPercentage,
						fobDecisionDetail?.otherCostPercentage
					)
				: calculateSuggestedFob(
						fobDecisionDetail?.targetLandedCost,
						fobDecisionDetail?.shippingPercentage,
						fobDecisionDetail?.warehousePercentage,
						fobDecisionDetail?.dutyPercentage,
						fobDecisionDetail?.otherCostPercentage
					)) ||
		fobDecisionResponse?.suggestedFobPrice?.toFixed(2) ||
		'';

	const suggestedFobNum = toNumberOrNull(suggestedFobPrice);
	const buyerMinRange = suggestedFobNum !== null ? (suggestedFobNum * 0.95).toFixed(2) : null;
	const buyerMaxRange = suggestedFobNum !== null ? (suggestedFobNum * 1.05).toFixed(2) : null;

	const handleFobSuggest = (fobSuggestPrice) => {
		const suggestedPrice = fobSuggestPrice || suggestedFobPrice;

		if (suggestedPrice !== '' && suggestedPrice !== null && suggestedPrice !== undefined) {
			setFobDecisionaDetail((prev) => ({
				...prev,
				targetedFobPrice: Number(suggestedPrice).toFixed(2)
			}));
		}
	};

	const handleCommercialCostVisibleChange = (isVisible) => {
		setIsCommercialCostVisible(isVisible);
		setSystemUserPreferences({ IS_COMMERCIAL_COST_VISIBLE: isVisible });
	};

	const handleShippingTypeItemChange = (shippingType) => {
		setShippingTypeItem(shippingType);
		setFobDecisionaDetail((prev) => ({
			...prev,
			shippingType
		}));
		setSystemUserPreferences({ SHIPPING_TYPE_ITEM: shippingType });
	};

	const scrollSimilarStyles = (direction) => {
		similarStylesRef.current?.scrollBy({
			left: direction * similarStylesRef.current.clientWidth,
			behavior: 'smooth'
		});
	};

	const handleFobDesision = (name, value) => {
		setFobDecisionaDetail((prev) => {
			const nextValue = value === '' ? '' : Number(value);
			const nextState = { ...prev, [name]: nextValue };

			const retailReference = name === 'retailReferencePrice' ? nextValue : prev.retailReferencePrice;
			const vatPercentage = name === 'vatPercentage' ? nextValue : prev.vatPercentage;
			const usdConversionRate =
				name === 'usdConversionRate' || name === 'usdConReference'
					? nextValue
					: (prev.usdConversionRate ?? prev.usdConReference ?? similarTypeData?.usdConversionRate);
			const retailPercentage = name === 'retailPercentage' ? nextValue : prev.retailPercentage;
			const shippingPercentage = name === 'shippingPercentage' ? nextValue : prev.shippingPercentage;
			const shippingValue = name === 'shippingValue' ? nextValue : prev.shippingValue;
			const warehousePercentage = name === 'warehousePercentage' ? nextValue : prev.warehousePercentage;
			const dutyPercentage = name === 'dutyPercentage' ? nextValue : prev.dutyPercentage;
			const otherCostPercentage = name === 'otherCostPercentage' ? nextValue : prev.otherCostPercentage;
			const commercialPercentage =
				name === 'commercialPercentage' ? nextValue : prev.commercialPercentage;

			const netValue =
				name === 'netValue' ? nextValue : calculateNetValue(retailReference, vatPercentage);
			const netRetailUsd = calculateNetRetailUsd(netValue, usdConversionRate);
			const targetLandedCost = calculateTargetLandedCost(netRetailUsd, retailPercentage);
			const targetedFobPrice = isCommercialCostVisible
				? calculateSuggestedFobWithCommercialCost(targetLandedCost, commercialPercentage)
				: shippingTypeItem === 'VALUE'
					? calculateSuggestedFobWithVlue(
							targetLandedCost,
							shippingValue,
							warehousePercentage,
							dutyPercentage,
							otherCostPercentage
						)
					: calculateSuggestedFob(
							targetLandedCost,
							shippingPercentage,
							warehousePercentage,
							dutyPercentage,
							otherCostPercentage
						);

			const suggestedFobNum = toNumberOrNull(targetedFobPrice);
			const minR = suggestedFobNum !== null ? (suggestedFobNum * 0.95).toFixed(2) : null;
			const maxR = suggestedFobNum !== null ? (suggestedFobNum * 1.05).toFixed(2) : null;
			const buyerBuyingRange = minR && maxR ? `$${minR} - $${maxR}` : (prev.buyerBuyingRange ?? '');

			nextState.usdConversionRate = usdConversionRate;
			nextState.usdConReference = usdConversionRate;
			nextState.netValue = netValue;
			nextState.netRetailUsd = netRetailUsd;
			nextState.targetLandedCost = targetLandedCost;
			nextState.buyerBuyingRange = buyerBuyingRange;
			nextState.commercialPercentage = commercialPercentage;

			if (
				name === 'shippingValue' ||
				name === 'shippingPercentage' ||
				name === 'warehousePercentage' ||
				name === 'dutyPercentage' ||
				name === 'otherCostPercentage' ||
				name === 'commercialPercentage' ||
				name === 'retailPercentage' ||
				name === 'retailReferencePrice' ||
				name === 'vatPercentage' ||
				name === 'netValue' ||
				name === 'usdConversionRate' ||
				name === 'usdConReference'
			) {
				nextState.targetedFobPrice = targetedFobPrice;
			}

			return nextState;
		});
	};

	const handleProductWindow = (productUrl) => {
		if (productUrl) {
			window.open(productUrl, '_blank');
		}
	};

	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};
	const openHistoryWiseViewModal = (selectedItem) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack: selectedItem.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: selectedItem.productId,
				onCloseViewModal: (id) => onCloseViewModal(id),
				source: 'HISTORY_WISE_VIEW'
			})
		);
	};

	const handleContinueToProduction = async () => {
		if (isEmptyFobInput || productionIsloading) return;
		const response = await handleFobSubmit({ showSuccessToast: true });
		if (response) {
			onViewChange?.('production-costing');
		}
	};

	return (
		<LoaderComponent loading={isMarketContextLoading || isSetFobDecisionLoading}>
			<div className="marketing-context-container mt-4">
				<div className="similar-and-rightbar d-flex gap-6">
					<div className="market-context-main flex-grow-1">
						<div className="market-value-boxes">
							<div className="market-value-box d-flex flex-column gap-1">
								<div className="market-value-box-header d-flex align-items-center">
									<span className="market-value-box-title regular-12 gray_dark_02 text-uppercase">
										Market median retail
									</span>
								</div>
								<p className="market-value-box-value regular-14 weight-600 gray_dark_01 mb-1">
									{similarTypeData?.currency}
									{similarTypeData?.medianRetailPrice}
								</p>
								<p className="market-value-box-description regular-12 gray_dark_02">
									What the brand sells at
								</p>
							</div>
							<div className="market-value-box d-flex flex-column gap-1">
								<span className="market-value-box-title regular-12 gray_dark_02 text-uppercase">
									Historical Avg FOB
								</span>
								<p className="market-value-box-value regular-14 weight-600 gray_dark_01 mb-1">
									${similarTableData?.averageFob}
								</p>
								<p className="market-value-box-description regular-12 gray_dark_02">
									What we&apos;ve closed before
								</p>
							</div>
						</div>

						<div className="similar-styles-panel">
							<div className="similar-styles-header d-flex align-items-center justify-content-between">
								<h4 className="semibold-16 mb-0">Similar Styles from {similarBrandName}</h4>
								<div className="d-flex align-items-center justify-content-end">
									<div className="similar-styles-meta regular-12 gray_dark_02 d-flex align-items-center">
										<span>{stylesToRender.length} MATCHES · MIP</span>
									</div>
									{stylesToRender.length > 0 && (
										<div className="slider-icons ml-2 d-flex gap-2">
											<button
												className="border p-1 bg-white d-flex align-items-center flex-column justify-content-center"
												type="button"
												aria-label="Previous styles"
												onClick={() => scrollSimilarStyles(-1)}
											>
												<CommonImage
													src="/icons/Left-arrow.svg"
													alt="slider-left"
													width={16}
													height={16}
												/>
											</button>
											<button
												className="border p-1 bg-white d-flex align-items-center flex-column justify-content-center"
												type="button"
												aria-label="Next styles"
												onClick={() => scrollSimilarStyles(1)}
											>
												<CommonImage
													src="/icons/right-arrow.svg"
													alt="slider-right"
													width={16}
													height={16}
												/>
											</button>
										</div>
									)}
								</div>
							</div>

							<div
								ref={similarStylesRef}
								className="similar-styles-cards d-flex scroll-x-label"
							>
								{stylesToRender.map((style) => (
									<div
										className="similar-style-card mb-2 d-flex flex-column position-relative cursor-pointer"
										key={`${style.name}-${style.sku}`}
										onClick={() => {
											mixPanelTrakingEvent({
												type: COSTING_MIX_PANEL_EVENTS.SIMILAR_STYLE_CLICK,
												body: {
													name: style?.name
												}
											});
											handleProductWindow(style?.productUrl);
										}}
									>
										<div className="similar-style-match position-absolute regular-12 weight-400 gray_dark_02">
											{style.match}
										</div>
										<div className="similar-style-visual">
											<CommonImage
												src={style.image || '/images/t-shirt-orange.PNG'}
												isStatic={!style.image}
												width="100%"
												alt="visual"
											/>
										</div>
										<div className="similar-style-info pt-2">
											<p className="similar-style-name regular-14 gray_dark_01">
												{style.name}
											</p>

											<div className="similar-style-price-row d-flex flex-column align-items-start justify-content-start gap-1 mt-2">
												{style?.oldPrice !== undefined &&
													style?.oldPrice !== null &&
													String(style.oldPrice) !== 'undefined' && (
														<span className="regular-12 gray_dark_01">
															Launch Price:{' '}
															{style?.currency === 'EUR' ? '€' : '$'}{' '}
															{style?.oldPrice}
														</span>
													)}
												{style?.newPrice !== undefined &&
													style?.newPrice !== null &&
													String(style.newPrice) !== 'undefined' && (
														<span className="regular-12 gray_dark_01">
															Current Price:
															{style?.currency === 'EUR' ? '€' : '$'}
															{style?.newPrice}
														</span>
													)}
											</div>
										</div>
									</div>
								))}
							</div>
							<div className="similar-styles-footer">
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">Median retail</p>
									<p className="semibold-16 mt-1">
										{similarTypeData?.currency}
										{similarTypeData?.medianRetailPrice}
									</p>
								</div>
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">Average retail</p>
									<p className="semibold-16 mt-1">
										{similarTypeData?.currency}
										{similarTypeData?.averageRetailPrice}
									</p>
								</div>
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">Retail range</p>
									<p className="semibold-16 mt-1">
										{similarTypeData?.currency}
										{similarTypeData?.retailPriceRange}
									</p>
								</div>
							</div>
						</div>
						{/* history table */}
						<div className="similar-styles-panel historical-orders-panel position-relative">
							<div className="header d-flex align-items-center justify-content-between px-4 pt-2">
								<h4 className="semibold-16 mb-0">Historical Nitex Orders</h4>
								<div className="d-flex align-items-center justify-content-end gap-2 py-1">
									<span className="gray_dark_02 border regular-12 px-2 py-1">
										{similarTableData?.similarOrders?.length} ORDERS
									</span>
									{similarTableData?.similarOrders?.length > 0 && (
										<HorizontalScroll elementReference={historyTableRef.current} />
									)}
								</div>
							</div>
							<div className="historical-orders-table scroll-x-label" ref={historyTableRef}>
								<div className="history-table scroll-y-label">
									<table className="table with-border height-sm mb-0" ref={historyTableRef}>
										<thead>
											<tr>
												<th className="regular-14">Image</th>
												<th className="regular-14 white-space-nowrap">Style No.</th>
												<th className="regular-14">Date</th>
												<th className="regular-14">Qty</th>
												<th className="regular-14 white-space-nowrap">Closed FOB</th>
												<th className="regular-14 white-space-nowrap">
													Factory Cost
												</th>
												<th className="regular-14 white-space-nowrap">Margin %</th>
												<th className="regular-14">Factory</th>
												<th className="regular-14">Match</th>
											</tr>
										</thead>
										<tbody>
											{Array.isArray(similarTableData?.similarOrders) &&
												similarTableData?.similarOrders.map((item, index) => (
													<tr key={index}>
														<td
															className="cursor-pointer img-column"
															onClick={() => {
																openHistoryWiseViewModal(
																	item,
																	item?.productId
																);
															}}
														>
															<CommonImage
																src={item?.imageUrl}
																isStatic={!item?.imageUrl}
																alt={item?.styleRefNumber}
																className="historical-order-image"
															/>
														</td>
														<td>
															<p
																className="gray_dark_01 weight-500 cursor-pointer white-space-nowrap"
																onClick={() => {
																	mixPanelTrakingEvent({
																		type: COSTING_MIX_PANEL_EVENTS.SIMILAR_ORDER_CLICK,
																		body: {
																			name: item?.styleRefNumber
																		}
																	});
																	openHistoryWiseViewModal(
																		item,
																		item?.productId
																	);
																}}
															>
																{item?.styleRefNumber}
															</p>
														</td>
														<td>
															<p className="white-space-nowrap">
																{item?.poReceivedDate
																	? String(item.poReceivedDate)
																			.split('T')[0]
																			.split(' ')[0]
																	: '--'}
															</p>
														</td>
														<td>
															<p>{item?.quantity}</p>
														</td>
														<td>
															<p>${item?.buyerFob}</p>
														</td>
														<td>
															<p>${item?.factoryFob}</p>
														</td>
														<td>
															<p>{item?.marginPercentage?.toFixed(2)}%</p>
														</td>

														<td>
															<p>{item?.factoryName}</p>
														</td>
														<td>
															<p>{item?.matchingScore?.toFixed(2)}%</p>
														</td>
													</tr>
												))}
										</tbody>
									</table>
								</div>
							</div>
							<div className="similar-styles-footer price-summary-box d-grid grid-columns-4">
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">Avg FOB</p>
									<p className="semibold-16 mt-1">${similarTableData?.averageFob}</p>
								</div>
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">Median FOB</p>
									<p className="semibold-16 mt-1">${similarTableData?.medianFob}</p>
								</div>
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">FOB Range</p>
									<p className="semibold-16 mt-1">${similarTableData?.fobRange}</p>
								</div>
								<div className="similar-summary-item">
									<p className="regular-12 gray_dark_02 text-uppercase">Avg Margin</p>
									<p className="semibold-16 mt-1">
										{similarTableData?.averageMarginPercentage}%
									</p>
								</div>
							</div>
						</div>
					</div>
					<FobDecisionPanel
						fobDecisionDetail={fobDecisionDetail}
						handleFobDesision={handleFobDesision}
						similarTypeData={similarTypeData}
						handleRetailRefClick={handleRetailRefClick}
						calculateNetValue={calculateNetValue}
						calculateNetRetailUsd={calculateNetRetailUsd}
						calculateTargetLandedCost={calculateTargetLandedCost}
						buyerMinRange={buyerMinRange}
						buyerMaxRange={buyerMaxRange}
						suggestedFobPrice={suggestedFobPrice}
						handleFobSuggest={handleFobSuggest}
						productionIsloading={productionIsloading}
						isEmptyFobInput={isEmptyFobInput}
						handleContinueToProduction={handleContinueToProduction}
						usdConversionPrice={similarTypeData?.usdConversionRate?.toFixed(2)}
						currency={similarTypeData?.currency}
						shippingTypeItem={shippingTypeItem}
						setShippingTypeItem={handleShippingTypeItemChange}
						isCommercialCostVisible={isCommercialCostVisible}
						setIsCommercialCostVisible={handleCommercialCostVisibleChange}
					/>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default MarketContextTarget;
